import React, { useState } from 'react';
import SectionBasePage from 'components/SectionBasePage';
import { Button, Switch } from 'UI';
import { Form } from 'antd';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanCulturesAction,
  createCultureAction,
  createVarietyAction,
  culturesEntitiesSelector,
  culturesIdsSelector,
  getCultureListAction,
  isLoadingCulturesSelector,
} from 'state/slices/products';
import { useBreadcrumbNavigation } from 'hooks/breadcrumb';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { unwrapResult } from '@reduxjs/toolkit';
import { Culture } from 'constants/interface';
import { ProductTypeEnum } from 'constants/product';
import { CreateVarietyData } from 'api/product';

import AddCultureForm, { AddCultureFormData } from './components/AddCultureForm';
import AddForm, { AddVarietyFormData } from './components/AddForm';
import AddProductForm, { AddProductFormData } from './components/AddProductForm';
import {
  MainSectionContainer,
  ProductSectionContainer,
  ProductsTitle,
  ProductTypeContainer,
  SwitchContainer,
  SwitchLabelContainer,
} from './style';

const ProductAddPage = (): JSX.Element => {
  const [addCultureForm] = Form.useForm<AddCultureFormData>();
  const [addVarietyForm] = Form.useForm<AddVarietyFormData>();
  const [addStandardProductForm] = Form.useForm<AddProductFormData>();
  const [addScionProductForm] = Form.useForm<AddProductFormData>();
  const [addStockProductForm] = Form.useForm<AddProductFormData>();
  const [isLoading, setLoading] = useState(false);
  const navigator = useBreadcrumbNavigation();
  const dispatch = useDispatch();

  const cultureEntities = useSelector(culturesEntitiesSelector);
  const cultureIds = useSelector(culturesIdsSelector);
  const cultureList = cultureIds.map((id) => cultureEntities[id]) as Culture[];
  const isLoadingCultures = useSelector(isLoadingCulturesSelector);
  const [selectedCulture, setSelectedCulture] = useState<Culture>();

  const [isStandardActive, setStandardActive] = useState(false);
  const [isScionActive, setScionActive] = useState(false);
  const [isStockActive, setStockActive] = useState(false);

  const onTypeToggle = (type: ProductTypeEnum, checked: boolean) => {
    if (type === ProductTypeEnum.STOCK) {
      if (checked) {
        setStandardActive(false);
        setScionActive(false);
        setStockActive(true);
      } else {
        setStockActive(false);
      }
    } else if (type === ProductTypeEnum.STANDARD) {
      if (checked) {
        setStandardActive(true);
        setStockActive(false);
      } else {
        setStandardActive(false);
      }
    } else if (type === ProductTypeEnum.SCION) {
      if (checked) {
        setScionActive(true);
        setStockActive(false);
      } else {
        setScionActive(false);
      }
    }
  };

  const onCultureSearch = (search: string) => {
    if (!search) {
      dispatch(cleanCulturesAction());
    } else {
      dispatch(getCultureListAction({
        search,
      }));
    }
  };

  const onCultureFormSubmit = async (data: AddCultureFormData) => {
    setLoading(true);
    try {
      const result = await dispatch(createCultureAction(data));
      const createdCulture = await unwrapResult(result);
      const successAlert = createAlertNotification(`
        Cultura ${createdCulture.name} criada com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      // setSelectedCulture(createdCulture);
      addCultureForm.resetFields();
      setLoading(false);
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar criar cultura. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const onVarietySaveClick = async () => {
    try {
      await addVarietyForm.validateFields();
      if (isStandardActive) {
        await addStandardProductForm.validateFields();
      }
      if (isScionActive) {
        await addScionProductForm.validateFields();
      }
      if (isStockActive) {
        await addStockProductForm.validateFields();
      }
      if (!selectedCulture?.id) {
        throw new Error();
      }
    } catch (err) {
      const errorAlert = createAlertNotification('Verifique os campos com erro.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    setLoading(true);
    const basicData = addVarietyForm.getFieldsValue();
    const productStandardData = addStandardProductForm.getFieldsValue();
    const productScionData = addScionProductForm.getFieldsValue();
    const productStockData = addStockProductForm.getFieldsValue();
    const varietyFullData: CreateVarietyData = {
      cultureId: selectedCulture.id,
      data: {
        name: basicData.name,
        seedConsumption: basicData.seedConsumption,
        notes: basicData.notes,
        productStandard: {
          periodFormation: productStandardData.periodFormation,
          periodGermination: productStandardData.periodGermination,
          loss: (productStandardData.loss || 0) / 100.0,
          isActive: isStandardActive,
        },
        productScion: {
          periodFormation: productScionData.periodFormation,
          periodGermination: productScionData.periodGermination,
          periodGrafting: productScionData.periodGermination,
          periodHealing: productScionData.periodGermination,
          loss: (productScionData.loss || 0) / 100.0,
          isActive: isScionActive,
        },
        productStock: {
          periodFormation: productStockData.periodFormation,
          periodGermination: productStockData.periodGermination,
          periodGrafting: productStockData.periodGermination,
          periodHealing: productStockData.periodGermination,
          loss: (productStockData.loss || 0) / 100.0,
          isActive: isStockActive,
        },
        isActive: true,
      },
    };

    try {
      const result = await dispatch(createVarietyAction(varietyFullData));
      await unwrapResult(result);
      const successAlert = createAlertNotification(`
        Variedade criada com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      navigator.toPrevious();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar criar variedade. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <SectionBasePage
      title="Novo produto"
      extra={(
        <Button
          loading={isLoading}
          onClick={onVarietySaveClick}
        >
          Salvar produto
        </Button>
      )}
    >
      <>
        <MainSectionContainer>
          <AddForm
            formHandler={addVarietyForm}
            onSubmit={onVarietySaveClick}
            isLoading={isLoading}
            onCultureSearch={onCultureSearch}
            onCultureSelect={(cultureId) => setSelectedCulture(cultureEntities[cultureId])}
            cultures={cultureList}
            isLoadingCultures={isLoadingCultures}
            addCultureForm={(
              <AddCultureForm
                formHandler={addCultureForm}
                onSubmit={onCultureFormSubmit}
                isLoading={isLoading}
              />
            )}
          />
        </MainSectionContainer>
        <ProductSectionContainer>
          <ProductsTitle>Selecione os tipos de formação</ProductsTitle>
          <ProductTypeContainer>
            <SwitchContainer>
              <Switch
                checked={isStandardActive}
                onChange={(checked) => onTypeToggle(ProductTypeEnum.STANDARD, checked)}
              />
              <SwitchLabelContainer>Pé franco</SwitchLabelContainer>
            </SwitchContainer>
            <AddProductForm
              formHandler={addStandardProductForm}
              onSubmit={onVarietySaveClick}
              isLoading={isLoading}
              isActive={isStandardActive}
            />
          </ProductTypeContainer>
          <ProductTypeContainer>
            <SwitchContainer>
              <Switch
                checked={isScionActive}
                onChange={(checked) => onTypeToggle(ProductTypeEnum.SCION, checked)}
              />
              <SwitchLabelContainer>Enxerto</SwitchLabelContainer>
            </SwitchContainer>
            <AddProductForm
              formHandler={addScionProductForm}
              onSubmit={onVarietySaveClick}
              isLoading={isLoading}
              isGrafting
              isActive={isScionActive}
            />
          </ProductTypeContainer>
          <ProductTypeContainer>
            <SwitchContainer>
              <Switch
                checked={isStockActive}
                onChange={(checked) => onTypeToggle(ProductTypeEnum.STOCK, checked)}
              />
              <SwitchLabelContainer>Porta-enxerto</SwitchLabelContainer>
            </SwitchContainer>
            <AddProductForm
              formHandler={addStockProductForm}
              onSubmit={onVarietySaveClick}
              isLoading={isLoading}
              isGrafting
              isActive={isStockActive}
            />
          </ProductTypeContainer>
        </ProductSectionContainer>
      </>
    </SectionBasePage>
  );
};

export default ProductAddPage;
